// ===============================================================
// 🎬 CreatePage — Studio EVO LIVE (enregistrement + upload MP4 H.264)
// ===============================================================
// - Enregistrement caméra + audio avec RecordRTC
// - Format vidéo : MP4 (H.264 style TikTok / Insta)
// - Timer (15/30/60s), pause, reprise
// - Filtres & FaceOverlay
// - Preview après enregistrement
// - Upload vers backend + BDD + profil
// ===============================================================

import React, { useRef, useState } from "react";
import axios from "../axiosInstance";              // ✅ axios centralisé (baseURL = https://live-evo.com/api)
import RecordRTC from "recordrtc";
import Carousel from "react-bootstrap/Carousel";
import { filtersConfig } from "../config/filtersConfig";
import FaceOverlay from "../components/FaceOverlay";

function CreatePage() {
  // -------------------------------------------------------------
  // 🎥 Références DOM et RecordRTC
  // -------------------------------------------------------------
  const videoRef = useRef(null);      // <video> de prévisualisation temps réel
  const recorderRef = useRef(null);   // instance RecordRTC
  const timerRef = useRef(null);      // interval pour le timer

  // -------------------------------------------------------------
  // 🎛 États de la page
  // -------------------------------------------------------------
  const [stream, setStream] = useState(null);           // MediaStream caméra+audio
  const [previewUrl, setPreviewUrl] = useState(null);   // URL pour preview après rec
  const [videoBlob, setVideoBlob] = useState(null);     // Blob de la vidéo enregistrée

  const [recording, setRecording] = useState(false);    // enregistrement en cours ?
  const [paused, setPaused] = useState(false);          // en pause ?
  const [duration, setDuration] = useState(30);         // durée cible (15/30/60s)
  const [elapsed, setElapsed] = useState(0);            // temps écoulé
  const [selectedFilter, setSelectedFilter] = useState(null); // filtre visuel

  // -------------------------------------------------------------
  // 📷 Activer la caméra
  // -------------------------------------------------------------
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
    } catch (err) {
      alert("❌ Accès caméra refusé ou indisponible.");
      console.error("Erreur getUserMedia :", err);
    }
  };

  // -------------------------------------------------------------
  // ⏺ Démarrer l’enregistrement (MP4 H.264 style TikTok/Insta)
  // -------------------------------------------------------------
  const startRecording = async () => {
    if (!stream) {
      alert("⚠️ Caméra non activée.");
      return;
    }

    // Nettoyer un éventuel ancien recorder
    if (recorderRef.current) {
      try {
        recorderRef.current.stopRecording();
      } catch {}
      recorderRef.current = null;
    }

    // Configuration RecordRTC pour MP4 / H.264
    recorderRef.current = new RecordRTC(stream, {
      type: "video",
      mimeType: "video/mp4",      // 🎯 MP4 H.264
      bitsPerSecond: 3_000_000,   // qualité vidéo (~3Mbps)
      disableLogs: true,
    });

    recorderRef.current.startRecording();

    setRecording(true);
    setPaused(false);
    setElapsed(0);

    // Timer pour stopper automatiquement à la fin de la durée
    timerRef.current = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + 1;
        if (next >= duration) {
          stopRecording(); // stop automatique
        }
        return next;
      });
    }, 1000);
  };

  // -------------------------------------------------------------
  // ⏸ Mettre en pause l’enregistrement
  // -------------------------------------------------------------
  const pauseRecording = () => {
    if (recorderRef.current && recording && !paused) {
      recorderRef.current.pauseRecording();
      setPaused(true);
      clearInterval(timerRef.current);
    }
  };

  // -------------------------------------------------------------
  // ▶️ Reprendre l’enregistrement
  // -------------------------------------------------------------
  const resumeRecording = () => {
    if (recorderRef.current && recording && paused) {
      recorderRef.current.resumeRecording();
      setPaused(false);

      // Relance du timer
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          const next = prev + 1;
          if (next >= duration) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
    }
  };

  // -------------------------------------------------------------
  // ⏹ Stopper l’enregistrement (version propre, sans callback)
  // -------------------------------------------------------------
  const stopRecording = async () => {
    if (!recorderRef.current) return;

    clearInterval(timerRef.current);

    try {
      await recorderRef.current.stopRecording();

      const blob = recorderRef.current.getBlob();
      if (!blob || !blob.size) {
        console.error("❌ Blob vidéo vide ou invalide");
        alert("❌ Impossible de récupérer la vidéo.");
        return;
      }

      const url = URL.createObjectURL(blob);
      setVideoBlob(blob);
      setPreviewUrl(url);

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }

      setRecording(false);
      setPaused(false);
    } catch (err) {
      console.error("❌ Erreur stopRecording :", err);
      alert("❌ Erreur lors de l'arrêt de l'enregistrement.");
    } finally {
      recorderRef.current = null;
    }
  };

  // -------------------------------------------------------------
  // 🗑 Supprimer l’enregistrement actuel (preview + blob)
  // -------------------------------------------------------------
  const deleteRecording = () => {
    clearInterval(timerRef.current);
    setVideoBlob(null);
    setPreviewUrl(null);
    setElapsed(0);
    setRecording(false);
    setPaused(false);
  };

  // -------------------------------------------------------------
  // 🔄 Recommencer : reset + réactiver la caméra
  // -------------------------------------------------------------
  const restartCamera = () => {
    deleteRecording();
    startCamera();
  };

  // -------------------------------------------------------------
  // 🚀 Publier la vidéo — envoi MP4 + meta au backend
  // -------------------------------------------------------------
  const publishVideo = async () => {
    if (!videoBlob) {
      alert("⚠️ Aucune vidéo à publier !");
      return;
    }

    const stored = JSON.parse(localStorage.getItem("currentUser") || "null");
    const email =
      (stored?.email ||
        localStorage.getItem("userEmail") ||
        localStorage.getItem("email") ||
        "").toString().trim().toLowerCase();
    const pseudo =
      (stored?.pseudo ||
        localStorage.getItem("userPseudo") ||
        localStorage.getItem("pseudo") ||
        "").toString().trim();

    const formData = new FormData();
    formData.append("video", videoBlob, "recording.mp4");
    formData.append("email", email);
    formData.append("pseudo", pseudo);
    formData.append("title", "Vidéo EVO");
    formData.append("description", "Créée avec EVO Recorder");

    try {
      // axiosInstance ajoute déjà /api → on met juste /videos/upload
      await axios.post("/videos/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("✅ Vidéo publiée !");
      deleteRecording();
    } catch (err) {
      console.error("❌ Erreur publication :", err.response?.data || err);
      alert("❌ Échec de l’envoi.");
    }
  };

  // ==============================================================
  // 🔥 RENDER DU STUDIO
  // ==============================================================
  return (
    <div className="container text-center py-4 position-relative">
      <h2 className="mb-3">🎬 Créer une Vidéo</h2>

      {/* Durée d’enregistrement */}
      <div className="mb-3">
        <button className="btn btn-outline-secondary rounded-pill mx-1" onClick={() => setDuration(15)}>15s</button>
        <button className="btn btn-outline-secondary rounded-pill mx-1" onClick={() => setDuration(30)}>30s</button>
        <button className="btn btn-outline-secondary rounded-pill mx-1" onClick={() => setDuration(60)}>60s</button>
      </div>

      <p>⏱ {elapsed}s / {duration}s - reste {Math.max(duration - elapsed, 0)}s</p>

      {/* Carrousel filtres / effets */}
      <div className="mb-3">
        <Carousel interval={null} indicators={false}>
          {["filters", "effects", "fondvert", "audio"].map((catId) => {
            const cat = filtersConfig.find((c) => c.id === catId);
            if (!cat) return null;
            return (
              <Carousel.Item key={cat.id}>
                <h5 className="text-center">{cat.name}</h5>
                <div className="d-flex gap-2 justify-content-center flex-wrap mt-2">
                  {filtersConfig
                    .filter((f) => f.id !== cat.id)
                    .map((f) => (
                      <button
                        key={f.id}
                        className="btn btn-outline-warning"
                        onClick={() => setSelectedFilter(f)}
                      >
                        {f.name}
                      </button>
                    ))}
                </div>
              </Carousel.Item>
            );
          })}
        </Carousel>
      </div>

      {/* Preview temps réel + overlay visage */}
      <div style={{ position: "relative", display: "inline-block" }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="rounded mb-3"
          style={{
            width: "320px",
            height: "220px",
            background: "black",
            border: recording ? "3px solid red" : "none",
          }}
        ></video>
        <FaceOverlay videoRef={videoRef} selectedFilter={selectedFilter} />
      </div>

      {/* Boutons de contrôle */}
      <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
        <button className="btn btn-primary rounded-pill px-4" onClick={startCamera}>📷 Activer caméra</button>
        <button className="btn btn-success rounded-pill px-4" onClick={startRecording} disabled={!stream || recording}>▶️ Démarrer</button>
        <button className="btn btn-warning rounded-pill px-4" onClick={pauseRecording} disabled={!recording || paused}>⏸ Pause</button>
        <button className="btn btn-info rounded-pill px-4" onClick={resumeRecording} disabled={!recording || !paused}>⏯ Reprendre</button>
        <button className="btn btn-danger rounded-pill px-4" onClick={stopRecording} disabled={!recording}>⏹ Arrêter</button>
        <button className="btn btn-outline-danger rounded-pill px-4" onClick={deleteRecording} disabled={!videoBlob}>🗑 Supprimer</button>
        <button className="btn btn-outline-secondary rounded-pill px-4" onClick={restartCamera}>🔄 Recommencer</button>
        <button className="btn btn-outline-success rounded-pill px-4" onClick={publishVideo} disabled={!videoBlob}>🚀 Publier</button>
      </div>

      {/* Aperçu post-enregistrement */}
      {previewUrl && (
        <div className="mt-4">
          <h4>📺 Aperçu</h4>
          <video
            key={previewUrl}
            src={previewUrl}
            controls
            className="rounded"
            style={{ width: "100%", maxWidth: "320px" }}
          ></video>
        </div>
      )}
    </div>
  );
}

export default CreatePage;
